import type { Business, CuratedPageItem, CuratedPageSection, Deal, EventRow } from "@/lib/supabase/types";
import type { LockedContentTeaser } from "@/lib/member-content";
import { formatDateTime } from "@/lib/utils";

export type CuratedResolvedDeal = Deal & { businesses: Business };
export type CuratedEntityType = "all" | "business" | "event" | "deal";
export type CuratedSortMode = "default" | "name_asc" | "name_desc";

export type CuratedFlatItem = {
  id: string;
  entityId: string;
  entityType: Exclude<CuratedEntityType, "all">;
  name: string;
  subtitle: string;
  meta: string;
  sectionTitle: string;
  sectionId: string;
  href: string;
  websiteUrl?: string | null;
  sortOrder: number;
  lockedTeaser?: LockedContentTeaser;
};

export function resolveCuratedItems(input: {
  items: CuratedPageItem[];
  sections: CuratedPageSection[];
  businesses: Business[];
  events: EventRow[];
  deals: CuratedResolvedDeal[];
  lockedTeasers?: LockedContentTeaser[];
}) {
  const businessById = Object.fromEntries(input.businesses.map((item) => [item.id, item]));
  const eventById = Object.fromEntries(input.events.map((item) => [item.id, item]));
  const dealById = Object.fromEntries(input.deals.map((item) => [item.id, item]));
  const sectionById = Object.fromEntries(input.sections.map((item) => [item.id, item]));
  const teaserByKey = Object.fromEntries((input.lockedTeasers ?? []).map((item) => [`${item.entityType}:${item.id}`, item]));
  const rows = input.items.map((item): CuratedFlatItem | null => {
    const section = sectionById[item.section_id];
    if (item.entity_type === "business") {
      const entity = businessById[item.entity_id];
      if (entity) return { id: item.id, entityId: item.entity_id, entityType: "business", name: entity.business_name, subtitle: entity.description ?? "", meta: "", sectionTitle: section?.title ?? "", sectionId: item.section_id, href: `/services/directory/${entity.slug}`, websiteUrl: entity.website_url, sortOrder: item.sort_order };
      const teaser = teaserByKey[`business:${item.entity_id}`];
      return teaser ? { id: item.id, entityId: item.entity_id, entityType: "business", name: teaser.title, subtitle: "", meta: "Members only", sectionTitle: section?.title ?? "", sectionId: item.section_id, href: `/services/directory/${teaser.slug}`, sortOrder: item.sort_order, lockedTeaser: teaser } : null;
    }
    if (item.entity_type === "event") {
      const entity = eventById[item.entity_id];
      if (entity) return { id: item.id, entityId: item.entity_id, entityType: "event", name: entity.name, subtitle: entity.suburb ?? "", meta: formatDateTime(entity.start_at), sectionTitle: section?.title ?? "", sectionId: item.section_id, href: `/events/${entity.slug}`, websiteUrl: entity.website_url, sortOrder: item.sort_order };
      const teaser = teaserByKey[`event:${item.entity_id}`];
      return teaser ? { id: item.id, entityId: item.entity_id, entityType: "event", name: teaser.title, subtitle: "", meta: "Members only", sectionTitle: section?.title ?? "", sectionId: item.section_id, href: `/events/${teaser.slug}`, sortOrder: item.sort_order, lockedTeaser: teaser } : null;
    }
    if (item.entity_type === "deal") {
      const entity = dealById[item.entity_id];
      if (entity) return { id: item.id, entityId: item.entity_id, entityType: "deal", name: entity.title, subtitle: entity.businesses.business_name, meta: entity.offer_text ?? "", sectionTitle: section?.title ?? "", sectionId: item.section_id, href: `/deals/${entity.slug}`, websiteUrl: entity.website_url, sortOrder: item.sort_order };
      const teaser = teaserByKey[`deal:${item.entity_id}`];
      return teaser ? { id: item.id, entityId: item.entity_id, entityType: "deal", name: teaser.title, subtitle: "", meta: "Members only", sectionTitle: section?.title ?? "", sectionId: item.section_id, href: `/deals/${teaser.slug}`, sortOrder: item.sort_order, lockedTeaser: teaser } : null;
    }
    return null;
  }).filter((item): item is CuratedFlatItem => item !== null);
  return { rows, businessById, eventById, dealById, sectionById };
}

export function filterCuratedItems(rows: CuratedFlatItem[], activeSection: string | null, entityType: CuratedEntityType, sort: CuratedSortMode) {
  return rows.filter((row) => (!activeSection || row.sectionId === activeSection) && (entityType === "all" || row.entityType === entityType)).sort((a, b) => {
    if (sort === "name_asc") return a.name.localeCompare(b.name);
    if (sort === "name_desc") return b.name.localeCompare(a.name);
    return a.sortOrder - b.sortOrder;
  });
}
